import { createFileRoute } from "@tanstack/react-router";
import { Cookie, Settings } from "lucide-react";
import { openCookiePreferences } from "@/lib/cookie-consent";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
      { title: "Cookie Policy — Recovery Trauma™" },
      {
        name: "description",
        content:
          "How Recovery Trauma™ uses cookies and similar technologies, and how you can manage your preferences.",
      },
      { property: "og:title", content: "Cookie Policy — Recovery Trauma™" },
      {
        property: "og:description",
        content: "Cookies we use and how to manage them.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/cookies" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/cookies" },
    ],
  }),
  component: CookiesPage,
});

function CookiesPage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 text-center">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <Cookie className="h-3.5 w-3.5" /> Cookies
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Cookie Policy
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Last updated: {new Date().toLocaleDateString("en-GB")}
        </p>
      </header>

      <div className="space-y-5 rounded-2xl border border-border bg-card p-6 text-sm leading-relaxed text-foreground/85">
        <p>
          Recovery Trauma™ uses cookies and similar technologies (such as
          browser local storage) to operate this site, remember your
          preferences, and — with your permission — measure how the platform
          is used.
        </p>

        <h2 className="font-serif text-xl text-foreground">Categories we use</h2>
        <ul className="list-disc space-y-2 ps-5">
          <li>
            <strong>Essential</strong> — required for sign-in, security, and
            core functionality. Always on. Examples: authentication session,
            disclaimer acknowledgement, cookie preferences themselves.
          </li>
          <li>
            <strong>Preferences</strong> — things you chose, such as theme,
            audio settings, favourites and drafts. Stored on your device only.
          </li>
          <li>
            <strong>Analytics</strong> — optional and off by default. If you
            agree, we record pages viewed, features used, referrer, campaign
            tags and coarse device information with a session and visitor
            identifier, in our own database. We do not use Google Analytics or
            any third-party analytics service.
          </li>
          <li>
            <strong>Essential operational measurement</strong> — a small number
            of sign-up and payment diagnostics run regardless of your analytics
            choice, so we can tell whether joining and paying are working. They
            are never used for advertising.
          </li>
          <li>
            <strong>Marketing</strong> — optional and off by default. We do not
            currently run any marketing cookies, pixels or advertising trackers,
            so this choice has no effect today. It is offered so your preference
            is already recorded if that ever changes.
          </li>
        </ul>
        <p>
          Embedded third-party content — Google Fonts, Trustpilot reviews and
          Spotify players where you paste a Spotify link — may receive your IP
          address or set its own cookies when it loads.
        </p>

        <h2 className="font-serif text-xl text-foreground">Managing your choices</h2>
        <p>
          You can change your cookie choices at any time. Removing cookies in
          your browser will reset your preferences.
        </p>
        <button
          onClick={openCookiePreferences}
          className="inline-flex items-center gap-2 rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
        >
          <Settings className="h-4 w-4" /> Open cookie preferences
        </button>

        <h2 className="font-serif text-xl text-foreground">UK / EU users</h2>
        <p>
          This policy is provided under the UK Privacy and Electronic
          Communications Regulations (PECR) and UK GDPR. Non-essential
          cookies are only set after you opt in.
        </p>

        <h2 className="font-serif text-xl text-foreground">US users</h2>
        <p>
          We do not sell personal information. If you are a resident of a US
          state with applicable privacy laws (such as California, Colorado,
          Connecticut, Texas, Virginia), you may exercise your rights via the
          Privacy & Data centre in your account.
        </p>

        <p className="text-xs text-muted-foreground">
          For full details of what we collect and why, see our{" "}
          <a href="/privacy" className="underline">
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
